import {
  FareInfographic,
  FareInfographicProps,
  computeInfographic
} from "@silversea-ssc/ui-components-react";
import { useLingui } from "@lingui/react/macro";

import React, { useCallback, useMemo } from "react";
import { infographicConfigForCruiseGroup } from "features/fare/infographic/infographic-config-for-cruise-group";
import {
  withStandardInclusions,
  activeInclusionsForAirNonAirCountry,
  infographicGroupLabels
} from "../../utils";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import { InclusionCode } from "@silversea-ssc/web-inclusions";
import { useInclusionsDrawer } from "features/inclusions/InclusionsDrawer/InclusionsDrawerProvider";
import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import { FareCode } from "@silversea-ssc/web-fares";
import { isPrintMedia } from "src/utils/is-print-media";
import { useStaticFareLabels } from "features/fare/use-static-fare-labels";

export type FareCardInfographicProps = {
  fareCode: FareCode;
  cruiseGroup: CruiseGroupCode | undefined;
  enabledInclusions: readonly InclusionCode[];
  inclusionsContentTokens: TokenValues;
  disableInclusionDrawer?: boolean;
} & Pick<FareInfographicProps, "expandable" | "isExpanded" | "variant">;

const FareCardInfographic: React.FC<FareCardInfographicProps> = ({
  fareCode,
  cruiseGroup,
  enabledInclusions,
  inclusionsContentTokens,
  disableInclusionDrawer = false,
  expandable,
  isExpanded,
  ...expandProps
}) => {
  const geolocation = useGeolocation();
  const { fareCodeLabel } = useStaticFareLabels();
  const isPrintMode = isPrintMedia();
  const { t } = useLingui();

  const infographicConfig = infographicConfigForCruiseGroup(cruiseGroup);

  const { onInclusionClick, inclusionsContent } = useInclusionsDrawer();

  const inclusionCodes = withStandardInclusions(
    activeInclusionsForAirNonAirCountry(
      enabledInclusions,
      geolocation.airCountry
    )
  );

  const infographicSteps = useMemo(
    () =>
      computeInfographic(
        infographicConfig,
        inclusionCodes,
        inclusionsContent,
        infographicGroupLabels(t)
      ),
    [t, inclusionCodes, inclusionsContent, infographicConfig]
  );

  const onClick = useCallback(
    (stepsGroupId) => {
      onInclusionClick({
        inclusionsContentTokens: inclusionsContentTokens,
        selectedStepGroupId: stepsGroupId,
        title: fareCodeLabel(fareCode),
        steps: infographicSteps
      });
    },
    [
      onInclusionClick,
      inclusionsContentTokens,
      fareCodeLabel,
      fareCode,
      infographicSteps
    ]
  );

  return (
    <React.Fragment>
      <FareInfographic
        steps={infographicSteps}
        onInclusionClick={disableInclusionDrawer ? undefined : onClick}
        expandable={expandable || isPrintMode}
        isExpanded={isExpanded || isPrintMode}
        {...expandProps}
      />
    </React.Fragment>
  );
};

export default FareCardInfographic;
